import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as UsersIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import Navbar from '../Navbar';
import { useTranslation } from 'react-i18next';

const drawerWidth = 260;

const ClientLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const menuItems = [
    { text: t('client.dashboard.title'), icon: <DashboardIcon />, path: '/client/dashboard' },
    { text: t('client.users.title'), icon: <UsersIcon />, path: '/client/users' },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <Navbar />
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'linear-gradient(180deg, #0d1b3e 0%, #1a237e 50%, #283593 100%)',
            color: '#ffffff',
            borderRight: 'none',
            boxShadow: '2px 0 10px rgba(0,0,0,0.2)',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ px: 2, py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 1,
            }}
          >
            <BusinessIcon sx={{ fontSize: 28, color: '#81d4fa' }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: '#e3f2fd',
                letterSpacing: '0.5px',
              }}
            >
              {t('client.portal')}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', pl: 0.5 }}
          >
            {t('client.userManagement')}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 2 }} />
        <List sx={{ px: 1, pt: 2 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    py: 1.2,
                    backgroundColor: isActive
                      ? 'rgba(255,255,255,0.15)'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive
                        ? 'rgba(255,255,255,0.2)'
                        : 'rgba(255,255,255,0.08)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? '#81d4fa' : 'rgba(255,255,255,0.7)',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#ffffff' : 'rgba(255,255,255,0.85)',
                    }}
                  />
                  {isActive && (
                    <Box
                      sx={{
                        width: 4,
                        height: 24,
                        borderRadius: 2,
                        backgroundColor: '#81d4fa',
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          backgroundColor: '#f5f7fa',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default ClientLayout;
